import React, { useState, useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';

// If you have a context for authentication:
import { useAuth } from '../../contexts/AuthContext';
// Import useNotification context
import { useNotification } from '../../contexts/NotificationContext'; // Adjust path if necessary
// Or import your own API utils:
import apiCall from '../../utils/api';
import FlecheEnvoiClair from '../../assets/fleche_envoi_clair.png';



// Styles
import {
  MobileHistoryContainer,
  Overlay,
  Header,
  CloseButtonWrapper,
  CloseButton,
  Title,
  Line,
  BoxAide,
  AideTitle,
  AideSubtitle,
  AideImage,
  BoxEmail,
  BoxEmailTitle,
  BoxEmailContent,
  BoxPhone,
  BoxPhoneTitle,
  BoxPhoneContent,
  BoxInfos,
  VersionText,
  InfosTitle,
  InfosText,
  BoxSocial,
  SocialTitle,
  SocialIconsWrapper,
  SocialIcon,
  BoxCGU,
  ActionsContainer,
  LogoutText,
  DeleteText,
  AideArrow,
  AideTextContainer,
  BoxDelete,
  BoxLogout,
  CGUImage,
  CGUText,
  ScrollContent,
} from './styles'; // Assuming styles are in './styles'

import needYouIcon from '../../assets/need_you_icon.png';
import arrowRightIcon from '../../assets/back_button_noir.png';
import instagramIcon from '../../assets/instagram-icon.png';
import tiktokIcon from '../../assets/tiktok-icon.png';
import cguIcon from '../../assets/cgu_icon.png';
import lettreIcon from '../../assets/lettre.png';
// Lazy-load popup component
const LazyPopupGlobale = React.lazy(() => import('../../components/PopupGlobale'));
// Helper to determine platform based on NotificationContext constants
const getPlatformInfo = () => {
  if (typeof window !== 'undefined') {
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers["push-permission-request"]) {
      return 'iOS App (via WebKit)';
    }
    if (/Android/i.test(navigator.userAgent) && window.matchMedia("(display-mode: standalone)").matches) {
      return 'Android App (TWA)';
    }
    if (/Android/i.test(navigator.userAgent)) {
      return 'Android Browser';
    }
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      return 'iOS Browser';
    }
    return 'Web Browser';
  }
  return 'Unknown (SSR)';
};


const Settings = () => {
  const navigate = useNavigate();
  const { logout } = useAuth(); 
  const { 
    fcmToken, 
    fcmError, 
    isSubscribed,
    permission, // Android permission from Notification.permission
    iosPermissionStatus // iOS permission from native bridge
  } = useNotification();

  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [instagramUsername, setInstagramUsername] = useState('');

  // State to handle popups
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await apiCall(
          `${process.env.REACT_APP_BACKEND_URL}/auth/user/`,
          'GET'
        );
        setEmail(response.email || '');
        setPhoneNumber(response.phone_number || '');
        setInstagramUsername(response.instagram_username || '');
      } catch (error) {
        console.error('Error retrieving user profile:', error);
      }
    };
    fetchUserProfile();
  }, []);

  const handleLogout = async () => {
    try {
      await logout(); 
      localStorage.clear(); 
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await apiCall(`${process.env.REACT_APP_BACKEND_URL}/auth/user/delete/`, 'DELETE');
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Account deletion error:', error);
    }
  };

  const BackButton = () => {
    navigate(-1); // Go back to the previous page
  };
  
  console.log("process.env.REACT_APP_VERSION :", process.env.REACT_APP_VERSION);
  const platformInfo = getPlatformInfo();

  return (
    <>
      <Overlay $isOpen={true} />
      <MobileHistoryContainer $isOpen={true}>
        <Header>
          <CloseButtonWrapper>
            <CloseButton onClick={BackButton} aria-label="Close menu">
              <img src={FlecheEnvoiClair} alt="Back" style={{ width: '24px', height: '24px', transform: 'rotate(0deg)' }} />
            </CloseButton>
          </CloseButtonWrapper>
          <Title>Paramètres</Title>
          <Line />
        </Header>

        <ScrollContent>
          <BoxAide onClick={() => navigate('/settings/aide-meo')}>
            <AideImage src={needYouIcon} />
            <AideTextContainer>
              <AideTitle>Aide nous à améliorer l'app !</AideTitle>
              <AideSubtitle>
                Un bug, une recommandation{'\n'}
                d'amélioration, de nouvelles{'\n'}
                fonctionnalités... Meo a besoin de toi !
              </AideSubtitle>
            </AideTextContainer>
            <AideArrow src={arrowRightIcon} />
          </BoxAide>

          <BoxEmail>
            <BoxEmailTitle>Ton email</BoxEmailTitle>
            <BoxEmailContent>{email}</BoxEmailContent>
          </BoxEmail>

          <BoxPhone onClick={() => navigate('/profile/modify/phone')}>
            <BoxPhoneTitle>Ton téléphone</BoxPhoneTitle>
            <BoxPhoneContent>{phoneNumber || 'Non renseigné'}</BoxPhoneContent>
          </BoxPhone>
          
          <BoxPhone onClick={() => navigate('/profile/modify/instagram')}>
            <BoxPhoneTitle>Ton Instagram</BoxPhoneTitle>
            <BoxPhoneContent>{instagramUsername ? `@${instagramUsername}` : 'Non renseigné'}</BoxPhoneContent>
          </BoxPhone>

          <BoxInfos>
            <InfosTitle>Infos sur l'app</InfosTitle>
            <InfosText>
              L'application Meoria est 100% gratuite. Pour qu'elle le reste,
              nous devons limiter le chat à 10 requêtes par jour et par utilisateur.
            </InfosText>
          </BoxInfos>

          <BoxSocial>
            <SocialTitle>Meo sur les réseaux !</SocialTitle>
            <SocialIconsWrapper>
              <a
                href="https://www.instagram.com/meoria_app/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SocialIcon src={instagramIcon} alt="Instagram" />
              </a>
              <a
                href="https://www.tiktok.com/@meoria_app"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SocialIcon src={tiktokIcon} alt="TikTok" />
              </a>
            </SocialIconsWrapper>
          </BoxSocial>

          <BoxCGU>
            <CGUImage src={cguIcon} alt="CGU" />
            <CGUText
              href="https://meoria.com/CGU.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Conditions d'utilisation
            </CGUText>
          </BoxCGU>
          
          <BoxCGU onClick={() => navigate('/social')}>
            <CGUImage src={lettreIcon} alt="Lettres" />
            <CGUText as="span">
              Social features bêta
            </CGUText>
          </BoxCGU>

          <ActionsContainer>
            <BoxLogout>
              <LogoutText onClick={() => setShowLogoutPopup(true)}>
                Me déconnecter
              </LogoutText>
            </BoxLogout>
            <BoxDelete>
              <DeleteText onClick={() => setShowDeletePopup(true)}>
                Supprimer mon compte
              </DeleteText>
            </BoxDelete>
          </ActionsContainer>
          
          <VersionText>
            Version {process.env.REACT_APP_VERSION || '1.0.1'}
          </VersionText>

          {/* ----- DEBUG INFO ----- */}
          <div style={{ 
            padding: '15px', margin: '10px', border: '1px dashed #ccc', borderRadius: '8px', 
            fontSize: '11px', color: '#333', textAlign: 'left', background: '#f9f9f9', wordBreak: 'break-all' 
          }}>
            <h4 style={{ marginTop: '0', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>
              Notification Debug Info:
            </h4>
            <p style={{margin: '3px 0'}}><strong>Platform Detected:</strong> {platformInfo}</p>
            <p style={{margin: '3px 0'}}><strong>FCM Token:</strong> {fcmToken || 'N/A'}</p>
            {fcmError && <p style={{ color: 'red', margin: '3px 0' }}><strong>Error:</strong> {fcmError}</p>}
            <p style={{margin: '3px 0'}}><strong>Is Subscribed (Context):</strong> {isSubscribed ? 'Yes' : 'No'}</p>
            {(platformInfo.includes('iOS App')) && (
              <p style={{margin: '3px 0'}}><strong>iOS Permission Status:</strong> {iosPermissionStatus}</p>
            )}
            {(platformInfo.includes('Android App')) && (
              <p style={{margin: '3px 0'}}><strong>Android Permission:</strong> {permission}</p>
            )}
          </div>
          {/* ----- END DEBUG INFO ----- */}

        </ScrollContent>
      </MobileHistoryContainer>      {showLogoutPopup && (
        <Suspense fallback={<div>Chargement...</div>}>
          <LazyPopupGlobale
            isVisible={showLogoutPopup}
            message="Êtes-vous sûr de vouloir vous déconnecter ?"
            cancelText="Annuler"
            confirmText="Continuer"
            onCancel={() => setShowLogoutPopup(false)}
            onConfirm={() => {
              handleLogout();
              setShowLogoutPopup(false);
            }}
          />
        </Suspense>
      )}      {showDeletePopup && (
        <Suspense fallback={<div>Chargement...</div>}>
          <LazyPopupGlobale
            isVisible={showDeletePopup}
            message="Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible"
            cancelText="Annuler"
            confirmText="Continuer"
            onCancel={() => setShowDeletePopup(false)}
            onConfirm={() => {
              handleDeleteAccount();
              setShowDeletePopup(false);
            }}
          />
        </Suspense>
      )}
    </>
  );
};

export default Settings;